import React, { useEffect, useState } from "react";
import {
  X, Cloud, AlertCircle, ShieldCheck, Info, RefreshCw, CheckCircle2,
  Copy, FileText, Settings2, KeyRound, Layers, Sparkles,
} from "lucide-react";
import { Badge } from "./Primitives";
import { cn } from "../lib/utils";
import { s3Config, s3Onboarding, createConnection, testConnection } from "../lib/s3Api";

// Generate a strong External ID scoped to a bucket.
// Shape: 1r-<tenant-slug>-<year>-<base32ish random>
// Base32-ish characters avoid ambiguous chars (0/O, 1/I/L).
function generateExternalId(bucket) {
  const year = new Date().getFullYear();
  const tenantSlug = (bucket || "customer")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 20) || "customer";
  const alphabet = "abcdefghjkmnpqrstuvwxyz23456789";
  const rand = crypto.getRandomValues(new Uint8Array(18));
  const random = Array.from(rand, b => alphabet[b % alphabet.length]).join("");
  return `1r-${tenantSlug}-${year}-${random}`;
}

// Amazon S3 cross-account configuration modal.
// Two tabs:
//   1. Onboarding — shows the backend role ARN + copy-ready trust & permission
//      policies for 1Rotary to paste into their AWS console.
//   2. Configuration — Role ARN / External ID / bucket / region / approved
//      prefix / encryption / requester pays. Saves via POST /api/s3/connections
//      then optionally runs Test Connection (real STS AssumeRole).
//
// Secrets: external_id is never returned by the backend after save. It is
// entered here once and sent server-side only.

const EMPTY = () => ({
  name: "",
  bucket: "",
  region: "ap-southeast-1",
  approved_prefix: "1rotary/prod/pos/",
  role_arn: "",
  external_id: "",
  encryption: "SSE-S3",
  kms_key_arn: "",
  requester_pays: false,
  session_duration: 3600,
  note: "",
});

export default function S3ConnectionModal({ open, onClose, onSaved }) {
  const [tab, setTab] = useState("onboarding");
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null); // "testing" | "success" | "fail"
  const [testDetail, setTestDetail] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [config, setConfig] = useState(null);
  const [snippets, setSnippets] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY());
    setErrors({});
    setSaving(false);
    setTesting(null);
    setTestDetail(null);
    setSavedId(null);
    setSnippets(null);
    setTab("onboarding");
    s3Config().then(setConfig).catch(() => setConfig(null));
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open || tab !== "onboarding") return;
    s3Onboarding({
      bucket: form.bucket,
      approved_prefix: form.approved_prefix,
      external_id: form.external_id,
      region: form.region,
    }).then(setSnippets).catch(() => setSnippets(null));
  }, [open, tab, form.bucket, form.approved_prefix, form.external_id, form.region]);

  if (!open) return null;

  function update(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = "Connection name is required.";
    if (!form.bucket.trim()) e.bucket = "Bucket name is required.";
    else if (!/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(form.bucket)) e.bucket = "Lowercase, 3-63 chars, no underscores.";
    if (!form.approved_prefix.trim()) e.approved_prefix = "Approved prefix is required.";
    if (!/^arn:aws:iam::\d{12}:role\/.+/.test(form.role_arn)) e.role_arn = "Must look like arn:aws:iam::123456789012:role/<name>.";
    if (!form.external_id.trim() || form.external_id.trim().length < 8) e.external_id = "External ID must be at least 8 characters.";
    if (form.encryption === "SSE-KMS" && !form.kms_key_arn.trim()) e.kms_key_arn = "KMS Key ARN is required when SSE-KMS is selected.";
    return e;
  }

  async function handleSave() {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSaving(true);
    try {
      const conn = await createConnection({
        name: form.name.trim(),
        bucket: form.bucket.trim(),
        region: form.region,
        approved_prefix: form.approved_prefix.trim(),
        role_arn: form.role_arn.trim(),
        external_id: form.external_id.trim(),
        encryption: form.encryption,
        kms_key_arn: form.kms_key_arn.trim(),
        requester_pays: form.requester_pays,
        session_duration: form.session_duration,
        note: form.note.trim(),
      });
      setSavedId(conn.id);
      // Run test immediately for a real trust/prefix probe
      setTesting("testing");
      const testRes = await testConnection(conn.id);
      const ok = !!(testRes.test && testRes.test.ok && testRes.test.list_ok);
      setTesting(ok ? "success" : "fail");
      setTestDetail(testRes.test);
      if (onSaved) onSaved(testRes.connection || conn);
    } catch (err) {
      setErrors({ save: err.message || "Save failed." });
      setTesting("fail");
      setTestDetail({ error: err.message });
    } finally {
      setSaving(false);
    }
  }

  const inputCls = (hasErr) => cn(
    "w-full h-9 px-3 bg-surface-2 border rounded-sm text-sm outline-none transition-colors",
    hasErr ? "border-critical focus:border-critical focus:ring-1 focus:ring-critical/40"
           : "border-border focus:border-primary focus:ring-1 focus:ring-primary/40",
  );

  return (
    <>
      <div data-testid="s3-modal-backdrop" onClick={onClose}
           className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 animate-fade-in" />
      <div data-testid="s3-modal"
           className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 pointer-events-none">
        <div className="w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col bg-bg border border-border rounded-md shadow-2xl pointer-events-auto animate-fade-in">
          {/* Header */}
          <div className="bg-bg/95 backdrop-blur-md border-b border-border px-5 py-3.5 flex items-start justify-between gap-4 shrink-0">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-sm bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <Cloud size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-1">Amazon S3 · Cross-Account Data Pipeline</div>
                <h2 className="font-display text-lg font-semibold tracking-tight leading-tight">Connect an S3 Bucket</h2>
                <p className="text-[12px] text-muted mt-0.5">STS AssumeRole with External ID · scoped to an approved folder · read-only</p>
              </div>
            </div>
            <button data-testid="s3-modal-close" onClick={onClose}
                    className="w-8 h-8 shrink-0 inline-flex items-center justify-center border border-border hover:border-primary hover:text-primary rounded-sm">
              <X size={14} />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-border px-5 pt-3 shrink-0 flex gap-2 bg-bg">
            <TabButton id="onboarding" active={tab === "onboarding"} onClick={() => setTab("onboarding")} icon={ShieldCheck}>
              1. Onboarding · Trust & Policies
            </TabButton>
            <TabButton id="configuration" active={tab === "configuration"} onClick={() => setTab("configuration")} icon={Settings2}>
              2. Configuration · Connect
            </TabButton>
            <TabButton id="layout" active={tab === "layout"} onClick={() => setTab("layout")} icon={Layers}>
              Recommended Layout
            </TabButton>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 p-5 space-y-5">
            {tab === "onboarding" && (
              <OnboardingTab
                config={config}
                snippets={snippets}
                form={form}
                update={update}
              />
            )}
            {tab === "configuration" && (
              <ConfigurationTab
                config={config}
                form={form}
                update={update}
                errors={errors}
                inputCls={inputCls}
              />
            )}
            {tab === "layout" && (
              <LayoutTab config={config} />
            )}
          </div>

          {/* Footer */}
          <div className="bg-bg/95 backdrop-blur-md border-t border-border px-5 py-3 flex items-center justify-between gap-3 shrink-0">
            <div className="text-[11px] flex items-center gap-2 min-w-0">
              {testing === "testing" && <><RefreshCw size={12} className="animate-spin text-info shrink-0" /> <span className="text-info">Assuming role & listing approved prefix…</span></>}
              {testing === "success" && <><CheckCircle2 size={12} className="text-success shrink-0" /> <span className="text-success truncate">Connected · {(testDetail?.sample_key_count ?? 0)} keys · {testDetail?.layout_hint || "no subject-area inferred"}</span></>}
              {testing === "fail" && <><AlertCircle size={12} className="text-critical shrink-0" /> <span className="text-critical truncate">{testDetail?.error || errors.save || "AssumeRole failed."}</span></>}
              {!testing && (
                <><Info size={12} className="text-muted shrink-0" /> <span className="text-muted">External ID is stored server-side, never returned.</span></>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {tab !== "configuration" ? (
                <button data-testid="s3-goto-config" onClick={() => setTab("configuration")}
                        className="px-3 py-1.5 text-xs bg-primary text-primary-fg font-semibold rounded-sm hover:opacity-90">
                  Next · Configure →
                </button>
              ) : (
                <>
                  <button data-testid="s3-cancel" onClick={onClose}
                          className="px-3 py-1.5 text-xs border border-border hover:bg-surface-2 rounded-sm">
                    Cancel
                  </button>
                  <button data-testid="s3-save" onClick={handleSave} disabled={saving || savedId}
                          className="px-3 py-1.5 text-xs bg-primary text-primary-fg font-semibold rounded-sm hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-1.5">
                    {saving && <RefreshCw size={11} className="animate-spin" />}
                    {savedId ? "Saved · Close when done" : "Save & Test Connection"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function TabButton({ id, active, onClick, icon: Icon, children }) {
  return (
    <button data-testid={`s3-tab-${id}`} onClick={onClick}
            className={cn("inline-flex items-center gap-1.5 px-3 h-9 text-[12px] rounded-t-sm transition-colors border-b-2",
              active ? "border-primary text-primary font-semibold" : "border-transparent text-muted hover:text-fg")}>
      <Icon size={12} /> {children}
    </button>
  );
}

/* -----------------------------------------------------------------------
   Onboarding tab
----------------------------------------------------------------------- */
function OnboardingTab({ config, snippets, form, update }) {
  if (!config) return <div className="text-muted text-sm">Loading backend config…</div>;
  const mech = config.workload_mechanism || "default-chain";
  return (
    <div className="space-y-4" data-testid="s3-onboarding-tab">
      <div className="p-3 border border-primary/40 bg-primary/5 rounded-sm space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-primary font-semibold flex items-center gap-1.5"><ShieldCheck size={11} /> Publish this to 1Rotary</div>
        <KVPair
          k="Our backend role ARN (trusted principal)"
          v={config.backend_role_arn}
          copyId="backend-role-arn"
          testid="onboarding-backend-role-arn"
        />
        <div className="text-[10.5px] text-muted leading-snug flex items-center gap-2 flex-wrap">
          <span>Mechanism:</span>
          <code className="font-mono-tabular text-fg" data-testid="onboarding-mechanism">{mech}</code>
          <span>· 1Rotary must add this ARN to the trust policy of the IAM role they create in <span className="font-mono-tabular text-fg">their</span> AWS account. Include a unique <span className="text-primary font-semibold">External ID</span> in the trust condition — you and 1Rotary agree on the value.</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">Bucket (for snippets)</div>
          <input data-testid="onboarding-bucket" value={form.bucket} onChange={e => update("bucket", e.target.value.toLowerCase())}
                 placeholder="e.g. 1rotary-data" className="w-full h-9 px-3 bg-surface-2 border border-border rounded-sm text-sm font-mono-tabular" />
        </label>
        <label className="space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">Approved prefix</div>
          <input data-testid="onboarding-prefix" value={form.approved_prefix} onChange={e => update("approved_prefix", e.target.value)}
                 placeholder="1rotary/prod/pos/" className="w-full h-9 px-3 bg-surface-2 border border-border rounded-sm text-sm font-mono-tabular" />
        </label>
        <label className="space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">External ID</div>
          <input data-testid="onboarding-external-id" value={form.external_id} onChange={e => update("external_id", e.target.value)}
                 placeholder="tenant-1r-2026-…" className="w-full h-9 px-3 bg-surface-2 border border-border rounded-sm text-sm font-mono-tabular" />
        </label>
      </div>

      <PolicyBlock title="Trust policy · attach to the customer role"
                   testid="onboarding-trust"
                   json={snippets?.trust_policy}
                   hint="This tells AWS to allow only our backend to assume the role, and only when the correct External ID is sent." />
      <PolicyBlock title="Permission policy · attach to the customer role"
                   testid="onboarding-permission"
                   json={snippets?.permission_policy}
                   hint="Grants read-only ListBucket + GetObject scoped to the approved prefix. No writes, no deletes, no ACL changes." />

      <div className="p-3 border border-info/30 bg-info/5 rounded-sm text-[11px] leading-relaxed text-fg/90">
        <div className="text-info font-semibold mb-1 flex items-center gap-1.5"><KeyRound size={11} /> What to send 1Rotary</div>
        <ol className="list-decimal ml-4 space-y-0.5">
          <li>The <span className="font-mono-tabular text-primary">backend role ARN</span> above (trusted principal).</li>
          <li>Both JSON snippets to paste into the role's Trust and Permission policies.</li>
          <li>Confirm the bucket, region, approved prefix, encryption mode, and whether Requester Pays is on.</li>
          <li>Come back to <span className="font-semibold">2. Configuration</span> and enter those values plus the External ID.</li>
        </ol>
      </div>
    </div>
  );
}

function PolicyBlock({ title, testid, json, hint }) {
  const text = json ? JSON.stringify(json, null, 2) : "// loading…";
  return (
    <div className="border border-border rounded-sm bg-surface-2/40" data-testid={testid}>
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="text-[11px] font-semibold text-fg flex items-center gap-1.5"><FileText size={11} /> {title}</div>
        <button data-testid={`${testid}-copy`} onClick={() => navigator.clipboard.writeText(text)}
                className="text-[10px] px-2 h-6 border border-border rounded-sm hover:border-primary hover:text-primary inline-flex items-center gap-1">
          <Copy size={10} /> Copy
        </button>
      </div>
      <pre className="p-3 text-[10.5px] font-mono-tabular text-fg/90 overflow-x-auto max-h-[240px] leading-snug">{text}</pre>
      {hint && <div className="border-t border-border px-3 py-1.5 text-[10.5px] text-muted">{hint}</div>}
    </div>
  );
}

function KVPair({ k, v, copyId, testid }) {
  return (
    <div className="flex items-center gap-2 min-w-0" data-testid={testid}>
      <span className="text-[10.5px] text-muted shrink-0">{k}</span>
      <code className="font-mono-tabular text-primary text-[11px] truncate flex-1">{v}</code>
      <button data-testid={`copy-${copyId}`} onClick={() => navigator.clipboard.writeText(v)}
              className="w-6 h-6 shrink-0 inline-flex items-center justify-center border border-border hover:border-primary hover:text-primary rounded-sm">
        <Copy size={10} />
      </button>
    </div>
  );
}

/* -----------------------------------------------------------------------
   Configuration tab
----------------------------------------------------------------------- */
function ConfigurationTab({ config, form, update, errors, inputCls }) {
  const regions = config?.supported_regions || [{ code: "ap-southeast-1", label: "Asia Pacific (Singapore)" }];
  return (
    <div className="space-y-4" data-testid="s3-config-tab">
      <Section title="Identity">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Connection name" required error={errors.name}>
            <input data-testid="s3-cfg-name" value={form.name} onChange={e => update("name", e.target.value)}
                   placeholder="e.g. S3 · POS 24-mo History" className={inputCls(errors.name)} />
          </Field>
          <Field label="AWS Region" required>
            <select data-testid="s3-cfg-region" value={form.region} onChange={e => update("region", e.target.value)} className={inputCls(false)}>
              {regions.map(r => <option key={r.code} value={r.code}>{r.code} · {r.label}</option>)}
            </select>
          </Field>
          <Field label="Bucket" required error={errors.bucket} className="md:col-span-2">
            <input data-testid="s3-cfg-bucket" value={form.bucket} onChange={e => update("bucket", e.target.value.toLowerCase())}
                   placeholder="e.g. 1rotary-data" className={cn(inputCls(errors.bucket), "font-mono-tabular")} />
          </Field>
          <Field label="Approved prefix" required error={errors.approved_prefix} className="md:col-span-2"
                 hint="All reads are scoped to this prefix. Include a trailing slash.">
            <input data-testid="s3-cfg-prefix" value={form.approved_prefix} onChange={e => update("approved_prefix", e.target.value)}
                   placeholder="1rotary/prod/pos/" className={cn(inputCls(errors.approved_prefix), "font-mono-tabular")} />
          </Field>
        </div>
      </Section>

      <Section title="Cross-account access">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Customer role ARN" required error={errors.role_arn} className="md:col-span-2">
            <input data-testid="s3-cfg-role-arn" value={form.role_arn} onChange={e => update("role_arn", e.target.value)}
                   placeholder="arn:aws:iam::123456789012:role/1rotary-ect-reader"
                   className={cn(inputCls(errors.role_arn), "font-mono-tabular")} />
          </Field>
          <Field label="External ID" required error={errors.external_id} className="md:col-span-2"
                 hint="Server-side secret — never returned by the API after save.">
            <div className="flex items-center gap-1.5">
              <input data-testid="s3-cfg-external-id" value={form.external_id} onChange={e => update("external_id", e.target.value)}
                     placeholder="tenant-1r-2026-…" className={cn(inputCls(errors.external_id), "font-mono-tabular flex-1")} />
              <button type="button" data-testid="s3-cfg-external-id-generate"
                      onClick={() => update("external_id", generateExternalId(form.bucket))}
                      className="h-9 px-3 border border-primary text-primary hover:bg-primary hover:text-primary-fg rounded-sm text-[11px] font-semibold inline-flex items-center gap-1 transition-colors">
                <Sparkles size={11} /> AI generate
              </button>
            </div>
          </Field>
        </div>
      </Section>

      <Section title="Security & options">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Server-side encryption">
            <select data-testid="s3-cfg-encryption" value={form.encryption} onChange={e => update("encryption", e.target.value)} className={inputCls(false)}>
              <option>SSE-S3</option>
              <option>SSE-KMS</option>
            </select>
          </Field>
          <Field label="Requester Pays" hint="Enable only if 1Rotary confirms their bucket has Requester Pays on.">
            <label data-testid="s3-cfg-requester-pays-wrap" className="flex items-center gap-2 h-9 px-3 bg-surface-2 border border-border rounded-sm">
              <input data-testid="s3-cfg-requester-pays" type="checkbox" checked={form.requester_pays} onChange={e => update("requester_pays", e.target.checked)}
                     className="accent-primary" />
              <span className="text-sm">Yes — our AWS account will pay egress</span>
            </label>
          </Field>
          {form.encryption === "SSE-KMS" && (
            <Field label="KMS Key ARN" required error={errors.kms_key_arn} className="md:col-span-2">
              <input data-testid="s3-cfg-kms" value={form.kms_key_arn} onChange={e => update("kms_key_arn", e.target.value)}
                     placeholder="arn:aws:kms:ap-southeast-1:…:key/…"
                     className={cn(inputCls(errors.kms_key_arn), "font-mono-tabular")} />
            </Field>
          )}
          <Field label="Session duration (seconds)" hint="Between 900 and 43200. Default 3600.">
            <input data-testid="s3-cfg-session-duration" type="number" min={900} max={43200} step={300}
                   value={form.session_duration} onChange={e => update("session_duration", parseInt(e.target.value || "3600", 10))}
                   className={cn(inputCls(false), "font-mono-tabular")} />
          </Field>
          <Field label="Notes" hint="Shown on the connection card." className="md:col-span-2">
            <textarea data-testid="s3-cfg-note" rows={2} value={form.note} onChange={e => update("note", e.target.value)}
                      className={cn(inputCls(false), "resize-none")}
                      placeholder="What does this connection carry? Which subject area? Audience?" />
          </Field>
        </div>
      </Section>

      {errors.save && (
        <div className="p-2.5 border border-critical/40 bg-critical/5 rounded-sm text-[12px] text-critical inline-flex items-center gap-2">
          <AlertCircle size={12} /> {errors.save}
        </div>
      )}
    </div>
  );
}

/* -----------------------------------------------------------------------
   Layout tab
----------------------------------------------------------------------- */
function LayoutTab({ config }) {
  const layout = config?.recommended_layout;
  if (!layout) return <div className="text-muted text-sm">Loading recommended layout…</div>;
  return (
    <div className="space-y-4" data-testid="s3-layout-tab">
      <div className="p-3 border border-primary/40 bg-primary/5 rounded-sm">
        <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-1">Recommended bucket layout</div>
        <p className="text-[12px] leading-relaxed">Connections aligned to this layout unlock <strong>subject-area auto-recognition</strong>, <code className="font-mono-tabular">_meta/</code>-driven schema discovery, and incremental discovery from <code className="font-mono-tabular">_meta/manifest/</code>. Non-compliant buckets still work through the same trust flow.</p>
        <pre className="mt-2 text-[10.5px] font-mono-tabular text-fg/90 bg-surface-2/60 border border-border rounded-sm p-2 overflow-x-auto">
{`s3://<bucket>/${layout.root}
├── pos/tx_headers/dt=YYYY-MM-DD/branch_id=BR-001/part-….parquet
├── inventory/snapshots/dt=…/branch_id=…/part-….parquet
├── supplier/po_lines/dt=…/part-….parquet
├── omnichannel/shopee_orders/dt=…/channel=shopee/part-….parquet
├── masters/dim_sku/version=YYYY-MM-DD/dim_sku.csv
├── external/weather/dt=…/weather.parquet
└── _control/{late_arriving,deletions,manifest_index.json}`}
        </pre>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">Subject areas</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {layout.subject_areas.map(s => (
            <div key={s.name} className="p-2.5 border border-border rounded-sm bg-surface-2/40" data-testid={`layout-subject-${s.name}`}>
              <div className="text-[11px] font-semibold text-fg">{s.label} <span className="text-muted font-mono-tabular text-[10px]">({s.name}/)</span></div>
              <div className="text-[10.5px] text-muted font-mono-tabular mt-0.5 truncate">{s.sample}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RulesList title="Partition rules" items={layout.partition_rules} testid="layout-partition-rules" />
        <RulesList title="Format rules" items={layout.format_rules} testid="layout-format-rules" />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">_meta sidecars per table</div>
        <div className="flex flex-wrap gap-2">
          {layout.meta_sidecars.map(m => (
            <Badge key={m} status="info"><code className="font-mono-tabular">{m}</code></Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

function RulesList({ title, items, testid }) {
  return (
    <div className="p-3 border border-border rounded-sm bg-surface-2/40" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">{title}</div>
      <ul className="space-y-1 text-[11.5px] text-fg/90">
        {items.map((it, i) => <li key={i} className="leading-snug"><span className="text-primary mr-1">·</span>{it}</li>)}
      </ul>
    </div>
  );
}

/* -----------------------------------------------------------------------
   Helpers
----------------------------------------------------------------------- */
function Section({ title, children }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-2 border-l-2 border-primary pl-2">{title}</div>
      {children}
    </section>
  );
}

function Field({ label, hint, required, error, children, className }) {
  return (
    <div className={cn("space-y-1", className)}>
      <label className="text-[11px] font-semibold uppercase tracking-widest text-muted flex items-center gap-1">
        {label}{required && <span className="text-critical">*</span>}
      </label>
      {children}
      {hint && !error && <div className="text-[10px] text-subtle">{hint}</div>}
      {error && <div className="text-[10px] text-critical inline-flex items-center gap-1"><AlertCircle size={9} />{error}</div>}
    </div>
  );
}
